import React, { useRef, useCallback, useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { PRIMITIVES } from '../constants';

export default function PrimitiveSidebar({
  template,
  templateResults,
  selectedPrimitive,
  onSelectPrimitive,
}) {
  const sidebarRef = useRef(null);

  // Group primitives by category
  const groupedPrimitives = PRIMITIVES.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  // Flatten primitives for arrow key navigation (memoized for stable deps)
  const flatPrimitives = useMemo(() => {
    return template
      ? template.steps?.map(step => step.primitive_id) || []
      : PRIMITIVES.map(p => p.id);
  }, [template]);

  // Handle arrow key navigation within sidebar
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

    e.preventDefault();
    const currentIndex = flatPrimitives.indexOf(selectedPrimitive);
    if (currentIndex === -1) return;

    let nextIndex;
    if (e.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : flatPrimitives.length - 1;
    } else {
      nextIndex = currentIndex < flatPrimitives.length - 1 ? currentIndex + 1 : 0;
    }

    const nextPrimitive = flatPrimitives[nextIndex];
    if (nextPrimitive) {
      onSelectPrimitive(nextPrimitive);
      // Focus the button after selection
      const sidebar = sidebarRef.current;
      if (sidebar) {
        const buttons = sidebar.querySelectorAll('.primitive-btn');
        buttons[nextIndex]?.focus();
      }
    }
  }, [flatPrimitives, selectedPrimitive, onSelectPrimitive]);

  // Get result status text for screen readers
  const getResultStatus = (idx) => {
    if (!templateResults?.[idx]) return '';
    return templateResults[idx].passed ? ', passed' : ', failed';
  };

  return (
    <nav
      ref={sidebarRef}
      className="explorer-sidebar"
      aria-label="API Primitives"
      onKeyDown={handleKeyDown}
    >
      <h3 id="primitives-heading">{template ? 'Template Steps' : 'Primitives'}</h3>
      {template ? (
        <ul className="primitives-list" role="listbox" aria-labelledby="primitives-heading">
          {template.steps?.map((step, idx) => (
            <li key={step.primitive_id} role="option" aria-selected={selectedPrimitive === step.primitive_id}>
              <button
                className={`primitive-btn ${selectedPrimitive === step.primitive_id ? 'active' : ''}`}
                onClick={() => onSelectPrimitive(step.primitive_id)}
                aria-label={`Step ${idx + 1}: ${step.name}${getResultStatus(idx)}`}
                aria-current={selectedPrimitive === step.primitive_id ? 'true' : undefined}
              >
                <span className="step-num" aria-hidden="true">{idx + 1}</span>
                {step.name}
                {templateResults?.[idx] && (
                  <span
                    className={`step-status ${templateResults[idx].passed ? 'passed' : 'failed'}`}
                    aria-hidden="true"
                  >
                    {templateResults[idx].passed ? <Check size={12} /> : <X size={12} />}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="primitives-grouped" role="listbox" aria-labelledby="primitives-heading">
          {Object.entries(groupedPrimitives).map(([category, prims]) => (
            <div key={category} className="primitive-group" role="group" aria-label={category}>
              <div className="primitive-group-label" id={`group-${category}`}>{category}</div>
              <ul className="primitives-list" aria-labelledby={`group-${category}`}>
                {prims.map(p => (
                  <li key={p.id} role="option" aria-selected={selectedPrimitive === p.id}>
                    <button
                      className={`primitive-btn ${selectedPrimitive === p.id ? 'active' : ''}`}
                      onClick={() => onSelectPrimitive(p.id)}
                      aria-label={`Select ${p.label} primitive`}
                      aria-current={selectedPrimitive === p.id ? 'true' : undefined}
                    >
                      {p.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}
